import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Dialog, Grid, Box, Typography, Divider } from '@material-ui/core';
import { Formik, Form } from 'formik';
import Input from 'components/Input';
import Button from 'components/Button';
import Loader from 'components/Loader';
import LoaderLabel from 'components/LoaderLabel';

import { VerifiedUserOutlined, ArrowForward } from '@material-ui/icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserAlt, faEnvelope, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import { relativePercentage } from 'helpers/common';

import { getPasswordRules } from './resources';

import { passwordUpdateValidations } from './validations';

const PrivacyTipIcon = require('../../assets/img/privacy-tip.svg');

export default function PasswordUpdate({ hasWarningStep = false, hasLogoutWarning = true, isLoading = false, isEmployeeEdit = false, isDismissable = false, isOpen, setIsOpen, onSuccess }) {
  const COUNTDOWN_INITIAL_VALUE = 10;
  const [countdown, setCountdown] = useState(COUNTDOWN_INITIAL_VALUE);

  const [isWarningStepOpen, setIsWarningStepOpen] = useState(hasWarningStep);
  const { isAdminUser } = useSelector(state => state.profile.submit);

  const isUserAdmin = +isAdminUser == 1;

  const { name, email } = useSelector(state => state.profile.submit);
  const { name: employeeName, email: employeeEmail } = useSelector(state => state.employeesRegistration.submit);

  useEffect(() => {
    if(countdown > 0) {
      setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
  }, [countdown]);

  const handleSubmit = values => {
    return onSuccess(values);
  }

  if(isWarningStepOpen) {
    return(
      <Dialog
        fullWidth
        maxWidth='sm'
        id='password-update-warning-modal'
        open={isOpen}
        onClose={() => {
          if(isUserAdmin) {
            return;
          }

          if(countdown > 0) {
            return;
          }

          setIsOpen(false);
        }}
        PaperProps={{
          style: {
            height: 'fit-content',
            overflowX: 'hidden'
          }
        }}
      >
        <Grid container spacing={2} style={{ padding: 30 }}>
          <Grid item xs={12}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <VerifiedUserOutlined style={{ color: '#43A047' }} />
              <Typography variant='body2' color='textSecondary'>
                COMUNICADO OFICIAL - JUMP TECNOLOGIA
              </Typography>
            </Box>
            <br />
            <Typography variant='body2'>
              <b>Olá, {name}</b>
            </Typography>
            <br />
            <Typography variant='body2'>
              A Jump Tecnologia segue crescendo, e com essa evolução a nossa responsabilidade com a segurança, estabilidade e proteção das informações dos nossos clientes se torna cada vez maior.
              <br />
              <br />
              Como parte do nosso ciclo de investimento, estamos implementando uma série de melhorias técnicas em toda a estrutura da plataforma.
              <br />
              <br />
              Dentro dessas ações, solicitamos que você realize a atualização da sua senha de acesso, adequando-a aos novos requisitos de segurança da plataforma. Essa medida faz parte do nosso processo contínuo de revisão de credenciais e reforço dos controles de acesso, adotado como prática padrão de segurança.
              <br />
              <br />
              Para redefinir sua senha, basta acessar a plataforma e seguir as instruções na tela de login. Caso tenha dúvidas ou precise de suporte, entre em contato pelo e-mail contato@jumptecnologia.com ou WhatsApp 21 9999 66 453.
              <br />
              <br />
              Agradecemos pela confiança e pela parceria. Seguimos trabalhando para entregar uma plataforma cada vez mais preparada para o crescimento do seu negócio.
              <br />
              <br />
            </Typography>
            <Typography variant='body2'>
              Atenciosamente,
            </Typography>
            <br />
            <Typography variant='body2'>
              <b>Gustavo</b>
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              CEO Jump Tecnologia
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid container xs={12}>
            <Grid item xs={countdown > 0 ? 10 : 12}>
              <Button
                fullWidth
                type='button'
                color='success'
                disabled={countdown > 0}
                endIcon={
                  <ArrowForward />
                }
                onClick={() => setIsWarningStepOpen(false)}
              >
                Entendi e Continuar
              </Button>
            </Grid>
            {(countdown > 0) && (
              <Grid item xs={2}>
                <LoaderLabel value={relativePercentage(countdown, COUNTDOWN_INITIAL_VALUE)}>
                  {countdown}
                </LoaderLabel>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Dialog>
    );
  }

  return(
    <Dialog
      fullWidth
      maxWidth='sm'
      id='password-update-modal'
      open={isOpen}
      onClose={() => {
        if(isDismissable) {
          setIsOpen(false);
        }

        if(isUserAdmin) {
          return;
        }

        setIsOpen(false);
      }}
      PaperProps={{
        style: {
          height: 'fit-content',
          overflowX: 'hidden'
        }
      }}
    >
      <Grid container spacing={2} style={{ padding: 30 }}>
        <Grid item xs={12}>
          <Grid container xs={12} justify='center'>
            <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '50px', height: '50px', backgroundColor: 'rgba(2, 42, 92, 0.2)', borderRadius: '50%' }}>
              <img src={PrivacyTipIcon} width='35' height='35' alt='' />
            </Box>
          </Grid>
          <Grid container xs={12} direction='column' alignItems='center' style={{ marginTop: 10 }}>
            <Typography variant='h5'>
              Proteja seus dados com uma senha forte
            </Typography>
            <Typography variant='body2' color='textSecondary' align='center' style={{ marginTop: 10 }}>
              Em confirmidade com o art. 46 da LGPD, a Jump está reforçando as medidas de segurança para prevenir acessos não autorizados. Atualize sua senha para continuar usando a plataforma com mais proteção.
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Loader isLoading={isLoading} />
          <Box style={{ display: isLoading ? 'none' : 'block' }}>
            {!isEmployeeEdit && (
              <Box style={{ marginTop: 10, marginBottom: 10 }}>
                <Box style={{ backgroundColor: '#F9FAFC', border: '1px solid #EDF0F5', borderRadius: 8, padding: 10 }}>
                  <Box style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 10 }}>
                    <FontAwesomeIcon
                      size='lg'
                      icon={faUserAlt}
                      color='#022A5C'
                    />
                    <Typography variant='body2'>
                      <b>{name || '--'}</b>
                    </Typography>
                  </Box>
                  <Box style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 10, marginTop: 10 }}>
                    <FontAwesomeIcon
                      size='lg'
                      icon={faEnvelope}
                      color='#97A1AC'
                    />
                    <Typography variant='body2' color='textSecondary'>
                      {email || '--'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            <Formik
              enableReinitialize={false}
              initialValues={{
                currentPassword: '',
                password: '',
                passwordConfirm: ''
              }}
              validationSchema={passwordUpdateValidations}
              validateOnChange={false}
              onSubmit={handleSubmit}
            >
              {({ ...formikProps }) => {
                return(
                  <Form>
                    <Grid container spacing={2}>
                      {!isEmployeeEdit && (
                        <Grid item xs={12}>
                          <Typography variant='body2'>
                            <b>Senha Atual</b>
                          </Typography>
                          <Input
                            name='currentPassword'
                            type='password'
                            label='Digite sua senha atual'
                            value={formikProps.values.currentPassword}
                            error={formikProps.errors.currentPassword}
                            helperText={formikProps.errors.currentPassword}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        {isEmployeeEdit && (
                          <Box style={{ marginTop: 10, marginBottom: 10 }}>
                            <Box style={{ backgroundColor: '#F9FAFC', border: '1px solid #EDF0F5', borderRadius: 8, padding: 10 }}>
                              <Box style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 10 }}>
                                <FontAwesomeIcon
                                  size='lg'
                                  icon={faUserAlt}
                                  color='#022A5C'
                                />
                                <Typography variant='body2'>
                                  <b>{employeeName || '--'}</b>
                                </Typography>
                              </Box>
                              <Box style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 10, marginTop: 10 }}>
                                <FontAwesomeIcon
                                  size='lg'
                                  icon={faEnvelope}
                                  color='#97A1AC'
                                />
                                <Typography variant='body2' color='textSecondary'>
                                  {employeeEmail || '--'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant='body2'>
                          <b>
                            {isEmployeeEdit ? (
                              'Nova senha do usuário'
                            ) : (
                              'Nova senha'
                            )}
                          </b>
                        </Typography>
                        <Input
                          name='password'
                          type='password'
                          label={isEmployeeEdit ? 'Digite a nova senha do usuário' : 'Digite sua nova senha'}
                          value={formikProps.values.password}
                          error={formikProps.errors.password}
                          helperText={formikProps.errors.password}
                          onChange={formikProps.handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant='body2'>
                          <b>
                            {isEmployeeEdit ? (
                              'Confirmar nova senha do usuário'
                            ) : (
                              'Confirmar nova senha'
                            )}
                          </b>
                        </Typography>
                        <Input
                          name='passwordConfirm'
                          type='password'
                          label={isEmployeeEdit ? 'Digite novamente a senha do usuário' : 'Digite a senha novamente'}
                          value={formikProps.values.passwordConfirm}
                          error={formikProps.errors.passwordConfirm}
                          helperText={formikProps.errors.passwordConfirm}
                          onChange={formikProps.handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        {getPasswordRules(formikProps.values.password)}
                      </Grid>
                      {hasLogoutWarning && (
                        <Grid item xs={12} style={{ marginTop: 10, marginBottom: 10 }}>
                          <Box style={{ backgroundColor: '#FCF3F2', border: '1px solid #C04F49', borderRadius: 8, padding: 10 }}>
                            <Box style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 10 }}>
                              <FontAwesomeIcon
                                size='lg'
                                icon={faTriangleExclamation}
                                color='#C04F49'
                              />
                              <Typography variant='body2' style={{ color: '#C04F49' }}>
                                <b>
                                  Usuário será forçado "logout", ou seja, a sessão no aplicativo operacional será encerrada automaticamente.
                                </b>
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}
                      {isEmployeeEdit && (
                        <Grid item xs={12}>
                          <Typography variant='body2'>
                            <b>Confirme sua senha para editar</b>
                          </Typography>
                          <Input
                            name='currentPassword'
                            type='password'
                            label='Confirme sua senha para editar'
                            value={formikProps.values.currentPassword}
                            error={formikProps.errors.currentPassword}
                            helperText={formikProps.errors.currentPassword}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          type='submit'
                          color='primary'
                        >
                          Criar Senha
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                )
              }}
            </Formik>
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
}